import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { Announcement } from '@/types/announcement';

const copy = {
  en: { title: 'Delete Announcement', desc: 'Are you sure you want to delete the announcement for', undone: 'This action cannot be undone.', cancel: 'Cancel', deleting: 'Deleting...', delete: 'Delete' },
  es: { title: 'Eliminar Anuncio', desc: '¿Estás seguro de que quieres eliminar el anuncio para', undone: 'Esta acción no se puede deshacer.', cancel: 'Cancelar', deleting: 'Eliminando...', delete: 'Eliminar' },
  fr: { title: "Supprimer l'annonce", desc: "Êtes-vous sûr de vouloir supprimer l'annonce pour", undone: 'Cette action est irréversible.', cancel: 'Annuler', deleting: 'Suppression...', delete: 'Supprimer' },
  pt: { title: 'Excluir Anúncio', desc: 'Tem certeza de que deseja excluir o anúncio para', undone: 'Esta ação não pode ser desfeita.', cancel: 'Cancelar', deleting: 'Excluindo...', delete: 'Excluir' },
  ar: { title: 'حذف الإعلان', desc: 'هل أنت متأكد من حذف الإعلان لـ', undone: 'لا يمكن التراجع عن هذا الإجراء.', cancel: 'إلغاء', deleting: 'جارٍ الحذف...', delete: 'حذف' },
  ja: { title: 'アナウンスを削除', desc: '本当にこのアナウンスを削除しますか：', undone: 'この操作は取り消せません。', cancel: 'キャンセル', deleting: '削除中...', delete: '削除' },
  ko: { title: '공지 삭제', desc: '정말 이 공지를 삭제하시겠습니까:', undone: '이 작업은 되돌릴 수 없습니다.', cancel: '취소', deleting: '삭제 중...', delete: '삭제' },
  id: { title: 'Hapus Pengumuman', desc: 'Apakah Anda yakin ingin menghapus pengumuman untuk', undone: 'Tindakan ini tidak dapat dibatalkan.', cancel: 'Batal', deleting: 'Menghapus...', delete: 'Hapus' },

  hi: { title: 'घोषणा हटाएँ', desc: 'क्या आप वाकई के लिए घोषणा हटाना चाहते हैं', undone: 'यह कार्रवाई पूर्ववत नहीं की जा सकती।', cancel: 'रद्द करें', deleting: 'हटाया जा रहा है...', delete: 'हटाएँ' },
};

interface DeleteAnnouncementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  announcement: Announcement | null;
  isDeleting: boolean;
  onConfirm: () => void;
}

export function DeleteAnnouncementDialog({
  open,
  onOpenChange,
  announcement,
  isDeleting,
  onConfirm,
}: DeleteAnnouncementDialogProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{c.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {c.desc} "{announcement?.recipient_name}"? {c.undone}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>{c.cancel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? c.deleting : c.delete}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
